import { Injectable } from '@angular/core';
import { BookingStepService } from '../modules/booking/services/booking-step.service';
import { GiftCardStepService } from '../modules/giftcard/services/giftcard-step.service';
import { LoggerService } from './logger/logger.service';
import { LoginStateService } from '../modules/login/services/login-state.service';
import { Subscriber, Observable, of, throwError } from 'rxjs';
import { OrderStateService } from '../modules/order/services/order-state.service';
import { PaymentStateService } from '../modules/payment/services/payment-state.service';
import { InactiveTimerService } from './inactive-timer.service';
import { ShopSettingsService } from '../modules/general/services/shop-settings.service';
import { TouchKeyboardService } from '../modules/vu/services/touch-keyboard.service';
import { PartnerCodeStateService } from '../modules/partner-code/services/partner-code-state.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { BookingOrderService } from '../modules/booking/services/booking-order.service';
import { PartnerStateService } from '../modules/partner/services/partner-state.service';
import { BookingProductStepService } from '../modules/booking/services/booking-product-step.service';

@Injectable()
export class AppStateService {

  private _initialize: boolean;

  constructor(
    private bookingStepService: BookingStepService,
    private giftCardStepService: GiftCardStepService,
    private orderStateService: OrderStateService,
    private paymentStateService: PaymentStateService,
    private logger: LoggerService,
    private inactiveTimerService: InactiveTimerService,
    private shopSettingsService: ShopSettingsService,
    private touchKeyboardService: TouchKeyboardService,
    private partnerCodeStateService: PartnerCodeStateService,
    private loginStateService: LoginStateService,
    private bookingOrderService: BookingOrderService,
    private partnerStateService: PartnerStateService,
    private bookingProductStepService: BookingProductStepService,
  ) {
  }

  reinitialize(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this._initialize = true;
        this.logger.info('AppStateService: reinitialize.');
        this.inactiveTimerService.clearTimer();
        this.partnerCodeStateService.reset();
      }),
      switchMap(_ => this.bookingStepService.reinitialize().pipe(
        catchError(error => {
          this.logger.info(`AppStateService: bookingStateService reinitialize error ${error}.`);
          return throwError(error);
        }),
      )),
      tap(_ => {
        this.logger.info('AppStateService: bookingStateService reinitialized.');

        if (this.shopSettingsService.isVUMode) {
          this.touchKeyboardService.hide();
        }
      }),
      switchMap(_ => this.bookingProductStepService.reinitialize().pipe(
        catchError(error => {
          this.logger.info(`AppStateService: bookingProductStateService reinitialize error ${error}.`);
          return throwError(error);
        }),
      )),
      tap(_ => {
        this.logger.info('AppStateService: bookingProductStateService reinitialized.');
      }),
      switchMap(_ => this.bookingOrderService.cancelVUActiveOrder()),
      switchMap(_ => {
        if (this.partnerCodeStateService.enabled) {
          return this.loginStateService.logout();
        } else {
          if (!this.shopSettingsService.isVUMode && this.shopSettingsService.shopSettings.partnerCodePayment) {
            return this.partnerStateService.initialize();
          }
        }
        return of(_);
      }),
      switchMap(_ => this.giftCardStepService.reinitialize().pipe(
        catchError(error => {
          this.logger.info(`AppStateService: giftCardStateService reinitialize error ${error}.`);
          return throwError(error);
        }),
      )),
      tap(_ => {
        this.logger.info('AppStateService: giftCardStateService reinitialized.');
        this.orderStateService.reset();
        this.logger.info('AppStateService: orderStateService reset.');
        this.paymentStateService.reset();
        this.logger.info('AppStateService: paymentStateService reset.');
        this._initialize = false;
      }),
    );
  }

  get initialize(): boolean {
    return this._initialize;
  }
}
